<template>
  <div>
    <v-container fluid>
      <!-- Header -->
      <v-toolbar color="primary" class="header-title text-white">
        <v-btn icon variant="text" @click="goBack" class="text-white">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="title">
          {{ metaCommunityName || 'Meta Community Graph' }}
        </v-toolbar-title>
        <v-spacer />
        <div class="d-flex align-center gap-2">
          <v-chip v-if="metadata.modularity" color="white" variant="outlined" size="small">
            Modularity: {{ metadata.modularity.toFixed(3) }}
          </v-chip>
          <v-chip v-if="metadata.totalNodes" color="white" variant="outlined" size="small">
            {{ metadata.totalNodes }} nodes
          </v-chip>
          <v-chip v-if="metadata.totalEdges" color="white" variant="outlined" size="small">
            {{ metadata.totalEdges }} edges
          </v-chip>
          <v-chip v-if="metadata.totalGroups" color="white" variant="outlined" size="small">
            {{ metadata.totalGroups }} communities
          </v-chip>
        </div>
      </v-toolbar>

      <v-row class="mt-2">
        <!-- Graph Area -->
        <v-col cols="12" md="9">
          <v-card class="graph-card" elevation="2">
            <v-card-text class="pa-0">
              <div v-if="loading" class="loading-container">
                <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
                <div class="mt-4">Loading graph data...</div>
              </div>
              <div v-else-if="error" class="error-container">
                <v-icon size="64" color="error">mdi-alert-circle</v-icon>
                <div class="mt-4">{{ error }}</div>
                <v-btn color="primary" class="mt-4" @click="loadGraph">Retry</v-btn>
              </div>
              <div v-else ref="graphContainer" class="graph-container"></div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Side Panel -->
        <v-col cols="12" md="3">
          <!-- Node Info Panel -->
          <v-card elevation="2">
            <v-card-title class="text-subtitle-1">
              <v-icon class="mr-2">mdi-information</v-icon>
              Node Information
            </v-card-title>
            <v-card-text>
              <div v-if="selectedNode">
                <div class="mb-2">
                  <strong>Label:</strong> {{ selectedNode.label }}
                </div>
                <div class="mb-2">
                  <strong>Community:</strong>
                  <v-chip size="x-small" :color="getGroupColor(selectedNode.group)">
                    {{ selectedNode.group + 1 }}
                  </v-chip>
                </div>
                <div class="mb-2">
                  <strong>Type:</strong> {{ selectedNode.entityType || 'entity' }}
                </div>
                <!-- Legacy single chunkId fallback (chunk-based) -->
                <div v-if="!selectedNode.entityId && selectedNode.chunkId" class="mb-2">
                  <strong>Chunk ID:</strong> {{ selectedNode.chunkId }}
                </div>
                <v-divider class="my-2"></v-divider>

                <!-- Entity description -->
                <div v-if="selectedNode.title" class="text-caption node-content mb-3" style="max-height: 150px; overflow-y: auto;">
                  {{ selectedNode.title }}
                </div>

                <!-- Source Chunks Panel (auto-loaded for entity-based) -->
                <div v-if="selectedNode.entityId">
                  <div class="d-flex align-center mb-2">
                    <v-icon size="small" class="mr-1">mdi-file-document-multiple</v-icon>
                    <strong class="text-caption">Source Chunks</strong>
                    <v-progress-circular v-if="loadingChunks" size="14" width="2" indeterminate class="ml-2"></v-progress-circular>
                    <span v-else-if="sourceChunks.length > 0" class="text-caption text-grey ml-1">({{ sourceChunks.length }})</span>
                  </div>

                  <!-- Chunk Dropdown Selector -->
                  <div v-if="sourceChunks.length > 0">
                    <v-select
                      v-model="selectedChunkIndex"
                      :items="chunkSelectItems"
                      item-title="title"
                      item-value="value"
                      density="compact"
                      variant="outlined"
                      class="mb-2"
                      hide-details
                    >
                      <template v-slot:prepend-inner>
                        <v-icon size="small">mdi-file-document</v-icon>
                      </template>
                    </v-select>

                    <!-- Selected Chunk Content -->
                    <v-card
                      v-if="selectedChunk"
                      variant="outlined"
                      class="pa-2"
                      density="compact"
                    >
                      <div class="text-caption text-grey mb-1">
                        <v-icon size="x-small" class="mr-1">mdi-file-document</v-icon>
                        Chunk #{{ selectedChunk.vectorId || selectedChunk.chunkId || (selectedChunkIndex + 1) }}
                        <span v-if="selectedChunk.score" class="ml-1">(Relevance: {{ (selectedChunk.score * 100).toFixed(0) }}%)</span>
                      </div>
                      <div class="text-caption chunk-content" style="white-space: pre-wrap; word-break: break-word; max-height: 250px; overflow-y: auto;">
                        {{ selectedChunk.content }}
                      </div>
                    </v-card>
                  </div>
                  <div v-else-if="!loadingChunks" class="text-caption text-grey">
                    No source chunks found
                  </div>
                </div>
              </div>
              <div v-else class="text-caption text-grey">
                Click a node to see its details
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>

export default {
  name: 'MetaCommunityGraph',
  data() {
    return {
      loading: true,
      error: null,
      metaCommunityId: null,
      metaCommunityName: '',

      // Graph data
      graphData: null,
      metadata: {
        modularity: null,
        totalNodes: 0,
        totalEdges: 0,
        totalGroups: 0,
        groups: [],
      },

      // graph renderer
      network: null,
      nodes: null,
      edges: null,
      graph: null,

      // UI state
      selectedNode: null,
      showEdgeLabels: false,  // No edge labels by default
      showArrows: true,       // Arrows enabled
      showShadows: true,      // Shadow enabled
      physicsEnabled: true,   // Physics disabling temporarily commented out

      // Source chunks state (for entity-based)
      sourceChunks: [],
      loadingChunks: false,
      selectedChunkIndex: 0,

      // Colors for groups (knowledge graph style)
      groupColors: [
        '#4A90D9', '#E8445A', '#3DBE7A', '#F5A623', '#9B59B6'
      ],
      groupBorderColors: [
        '#1A4C8A', '#8C1528', '#155C35', '#8A4E0A', '#4A1F6E'
      ],
      edgeColors: [
        '#4A90D9', '#E8445A', '#3DBE7A', '#F5A623', '#9B59B6'
      ],

      // sigma runtime
      _sigmaState: null,
      _stabilizeTimer: null,
      _dragStabilizeTimer: null,
      _dragEndTimer: null,
      _tooltipTimer: null,
    };
  },
  watch: {
    showEdgeLabels() {
      this.updateEdgeLabels();
    },
    showArrows() {
      this.updateArrows();
    },
    showShadows() {
      this.updateShadows();
    },
    sourceChunks() {
      // Reset selection when chunks change
      this.selectedChunkIndex = 0;
    },
  },
  computed: {
    // Dropdown items for chunk selector
    chunkSelectItems() {
      return this.sourceChunks.map((chunk, index) => {
        const chunkId = chunk.vectorId || chunk.chunkId || (index + 1);
        const relevance = chunk.score ? ` (${(chunk.score * 100).toFixed(0)}%)` : '';
        const preview = chunk.content ? chunk.content.substring(0, 40) + '...' : '';
        return {
          title: `Chunk #${chunkId}${relevance} - ${preview}`,
          value: index
        };
      });
    },
    // Currently selected chunk
    selectedChunk() {
      if (this.sourceChunks.length > 0 && this.selectedChunkIndex >= 0 && this.selectedChunkIndex < this.sourceChunks.length) {
        return this.sourceChunks[this.selectedChunkIndex];
      }
      return null;
    },
  },
  mounted() {
    this.metaCommunityId = this.$route.params.id;
    this.loadGraph();
  },
  beforeUnmount() {
    this.cleanupNetwork();
  },
  methods: {
    cleanupNetwork() {
      if (this._stabilizeTimer) {
        clearTimeout(this._stabilizeTimer);
        this._stabilizeTimer = null;
      }
      if (this._dragStabilizeTimer) {
        clearTimeout(this._dragStabilizeTimer);
        this._dragStabilizeTimer = null;
      }
      if (this._dragEndTimer) {
        clearTimeout(this._dragEndTimer);
        this._dragEndTimer = null;
      }
      if (this._tooltipTimer) {
        clearTimeout(this._tooltipTimer);
        this._tooltipTimer = null;
      }
      if (this._sigmaState?.containerKeydownHandler && this.$refs.graphContainer) {
        this.$refs.graphContainer.removeEventListener('keydown', this._sigmaState.containerKeydownHandler);
      }
      if (this._sigmaState?.tooltipEl) {
        this._sigmaState.tooltipEl.remove();
      }
      if (this._sigmaState?.navEl) {
        this._sigmaState.navEl.remove();
      }
      if (this._sigmaState?.crossEdgeCanvas) {
        this._sigmaState.crossEdgeCanvas.remove();
      }
      if (this.network && typeof this.network.kill === 'function') {
        this.network.kill();
      }
      this.network = null;
      this.graph = null;
      this._sigmaState = null;
    },

    goBack() {
      this.$router.push({ name: 'MetaCommunityManagement' });
    },

    truncateSummary(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    loadGraph() {
      const self = this;
      self.loading = true;
      self.error = null;

      this.$call.GetMetaCommunityGraph(
        self.metaCommunityId,
        (response) => {
          if (response.status === 0 && response.data) {
            self.graphData = response.data;
            self.metadata = response.data.metadata || {};
            self.metaCommunityName = self.metadata.metaCommunityName || 'Meta Community Graph';
            self.loading = false;

            // Initialize network after DOM update
            self.$nextTick(() => {
              self.initNetwork();
            });
          } else {
            self.error = response.message || 'Failed to load graph data';
            self.loading = false;
          }
        },
        (error) => {
          console.error(error);
          self.error = 'Failed to load graph data';
          self.loading = false;
        }
      );
    },

    async initNetwork() {
      const self = this;
      const container = self.$refs.graphContainer;
      if (!container) return;

      self.cleanupNetwork();

      const [{ default: Sigma }, { default: Graph }, randomLayoutModule, nodeBorderModule, edgeCurveModule] = await Promise.all([
        import('sigma'),
        import('graphology'),
        import('graphology-layout/random'),
        import('@sigma/node-border'),
        import('@sigma/edge-curve'),
      ]);
      const randomLayout = randomLayoutModule.default || randomLayoutModule;
      const NodeBorderProgram = nodeBorderModule.createNodeBorderProgram
        ? nodeBorderModule.createNodeBorderProgram({
            borders: [
              { size: { value: 0.1 }, color: { attribute: 'borderColor' } },
              { size: { fill: true }, color: { attribute: 'color' } },
            ],
          })
        : nodeBorderModule.NodeBorderProgram;
      const EdgeCurveProgram = edgeCurveModule.default || edgeCurveModule.EdgeCurveProgram;
      const EdgeCurvedArrowProgram = edgeCurveModule.EdgeCurvedArrowProgram || edgeCurveModule.default;

      const positionStorageKey = 'meta-community-graph-positions';
      const loadStoredPositions = () => {
        if (!self.metaCommunityId) return null;
        try {
          const raw = localStorage.getItem(positionStorageKey);
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            const matched = parsed.find(item =>
              item
              && String(item.metaCommunityId) === String(self.metaCommunityId)
              && item.positions
              && typeof item.positions === 'object'
            );
            return matched ? matched.positions : null;
          }
          if (
            parsed
            && typeof parsed === 'object'
            && parsed.positions
            && typeof parsed.positions === 'object'
            && String(parsed.metaCommunityId) === String(self.metaCommunityId)
          ) {
            return parsed.positions;
          }
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (error) {
          console.warn('Failed to read saved graph positions:', error);
          return null;
        }
      };
      const storedPositions = loadStoredPositions();
      let cachedPositionMatchCount = 0;
      const graphNodeIds = [];

      const throttle = (fn, waitMs = 33) => {
        let timerId = null;
        let lastRun = 0;
        return (...args) => {
          const now = Date.now();
          const remaining = waitMs - (now - lastRun);
          if (remaining <= 0) {
            lastRun = now;
            fn(...args);
            return;
          }
          if (timerId) return;
          timerId = setTimeout(() => {
            timerId = null;
            lastRun = Date.now();
            fn(...args);
          }, remaining);
        };
      };

      const groupInfo = {};
      if (self.metadata.groups) {
        self.metadata.groups.forEach(g => {
          groupInfo[g.groupIndex] = g;
        });
      }

      const rawNodeMap = new Map();
      self.graphData.nodes.forEach((node) => {
        rawNodeMap.set(String(node.id), node);
      });

      const nodeDegrees = {};
      self.graphData.nodes.forEach((node) => {
        nodeDegrees[String(node.id)] = 0;
      });
      self.graphData.edges.forEach((edge) => {
        const from = String(edge.from);
        const to = String(edge.to);
        if (nodeDegrees[from] !== undefined) nodeDegrees[from]++;
        if (nodeDegrees[to] !== undefined) nodeDegrees[to]++;
      });

      const nodesData = self.graphData.nodes.map((node) => {
        const id = String(node.id);
        const colorGroup = node.group % self.groupColors.length;
        const groupColor = self.groupColors[colorGroup];
        const tonedNodeColor = groupColor;
        const borderColor = self.groupBorderColors[colorGroup % self.groupBorderColors.length] || self.darkenColor(tonedNodeColor, 26);
        const group = groupInfo[node.group] || {};
        const tooltip = self.buildNodeTooltip(node, group);
        const nodeLabel = node.entityName || node.label || `Node ${node.id}`;
        const degree = nodeDegrees[id] || 0;
        const nodeSize = Math.min(4.2 + (degree * 0.62), 14);
        const rawStoredPosition = storedPositions ? (storedPositions[node.id] || storedPositions[id]) : null;
        const hasStoredPosition = rawStoredPosition
          && Number.isFinite(rawStoredPosition.x)
          && Number.isFinite(rawStoredPosition.y);
        if (hasStoredPosition) cachedPositionMatchCount++;
        graphNodeIds.push(id);

        return {
          id,
          originalId: node.id,
          label: nodeLabel,
          originalLabel: nodeLabel,
          tooltipHtml: tooltip,
          group: node.group,
          colorGroup,
          entityType: node.entityType || 'chunk',
          entityId: node.entityId,
          chunkId: node.chunkId,
          chunkIds: node.chunkIds || [],
          vectorIds: node.vectorIds || [],
          fullContent: node.content || node.title,
          sources: node.sources || [],
          color: tonedNodeColor,
          baseColor: tonedNodeColor,
          borderColor,
          baseBorderColor: borderColor,
          type: 'border',
          size: nodeSize,
          baseSize: nodeSize,
          degree,
          hidden: false,
          forceLabel: false,
          shadow: true,
          isAnchor: false,
          ...(hasStoredPosition ? { x: rawStoredPosition.x, y: rawStoredPosition.y } : {}),
        };
      });
      const hasCompleteStoredPositions = nodesData.length > 0 && cachedPositionMatchCount === nodesData.length;
      const overviewNodeHidingEnabled = nodesData.length > 300;
      const TARGET_NODE_GAP_PX = 72;
      const CONNECTED_NODE_GAP_PX = 96;

      const useCommunityAnchors = false;
      const groupAnchorIds = {};
      const anchorNodesData = [];
      if (useCommunityAnchors) {
        const presentGroups = [...new Set(nodesData.map(n => n.group))];
        presentGroups.forEach((groupIndex) => {
          const anchorId = `__group_anchor_${groupIndex}`;
          groupAnchorIds[groupIndex] = anchorId;
          anchorNodesData.push({
            id: anchorId,
            originalId: anchorId,
            label: '',
            originalLabel: '',
            group: groupIndex,
            size: 1,
            baseSize: 1,
            color: 'rgba(0,0,0,0)',
            baseColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(0,0,0,0)',
            type: 'border',
            hidden: false,
            forceLabel: false,
            shadow: false,
            isAnchor: true,
            degree: 0,
            tooltipHtml: '',
          });
        });
      }

      const allNodeSizes = nodesData.map(n => n.size).sort((a, b) => a - b);
      const percentile80Index = Math.floor(allNodeSizes.length * 0.8);
      const bigNodeThreshold = allNodeSizes[percentile80Index] || 10;
      const overviewVisibleNodeIds = new Set(
        nodesData
          .filter(n => (nodeDegrees[n.id] || 0) === 0 || n.size >= bigNodeThreshold)
          .map(n => n.id)
      );

      const EDGE_THICKNESS = 1.2;
      const CROSS_EDGE_THICKNESS = 1;
      const SELECTED_EDGE_THICKNESS = 1.4;

      const edgesData = self.graphData.edges.map((edge, index) => {
        const from = String(edge.from);
        const to = String(edge.to);
        const sourceNode = rawNodeMap.get(from);
        const targetNode = rawNodeMap.get(to);
        const isInternal = sourceNode && targetNode && sourceNode.group === targetNode.group;

        const edgeColor = sourceNode
          ? self.edgeColors[(sourceNode.group || 0) % self.edgeColors.length] || '#185FA5'
          : '#185FA5';

        const tooltip = self.buildEdgeTooltip(edge, sourceNode, targetNode, isInternal);
        const edgeLabel = edge.relation || (edge.weight ? edge.weight.toFixed(2) : '');

        return {
          id: `e_${index}`,
          from,
          to,
          relation: edge.relation,
          tooltipHtml: tooltip,
          label: edgeLabel,
          originalLabel: edgeLabel,
          color: edgeColor,
          baseColor: edgeColor,
          type: isInternal ? 'curvedArrow' : 'curvedCross',
          size: EDGE_THICKNESS,
          baseSize: EDGE_THICKNESS,
          curvature: isInternal ? 0.18 : 0.3,
          hidden: false,
          isCrossCommunity: !isInternal,
          isAnchorEdge: false,
        };
      });

      const nodeNeighborMap = new Map();
      nodesData.forEach((node) => {
        nodeNeighborMap.set(node.id, new Set());
      });
      edgesData.forEach((edge) => {
        if (nodeNeighborMap.has(edge.from) && nodeNeighborMap.has(edge.to)) {
          nodeNeighborMap.get(edge.from).add(edge.to);
          nodeNeighborMap.get(edge.to).add(edge.from);
        }
      });

      const groupAttractionEdges = useCommunityAnchors
        ? nodesData.map((node, index) => ({
          id: `__group_anchor_edge_${index}`,
          from: groupAnchorIds[node.group],
          to: node.id,
          size: 0.1,
          weight: 9.0,
          color: 'rgba(0,0,0,0)',
          baseColor: 'rgba(0,0,0,0)',
          type: 'curved',
          hidden: false,
          isCrossCommunity: false,
          isAnchorEdge: true,
          label: '',
          originalLabel: '',
          tooltipHtml: '',
        }))
        : [];

      const graph = new Graph({ type: 'directed', multi: true, allowSelfLoops: true });
      [...nodesData, ...anchorNodesData].forEach((node) => {
        graph.addNode(node.id, node);
      });
      [...edgesData, ...groupAttractionEdges].forEach((edge) => {
        try {
          graph.addDirectedEdgeWithKey(edge.id, edge.from, edge.to, edge);
        } catch {
          graph.addEdgeWithKey(edge.id, edge.from, edge.to, edge);
        }
      });

      // Sigma requires all nodes to have numeric x/y before renderer init.
      graph.forEachNode((nodeId, attrs) => {
        const hasX = Number.isFinite(attrs.x);
        const hasY = Number.isFinite(attrs.y);
        if (hasX && hasY) return;
        const seed = attrs.group || 0;
        const jitter = 20;
        graph.setNodeAttribute(nodeId, 'x', (seed % 7) * 30 + (Math.random() - 0.5) * jitter);
        graph.setNodeAttribute(nodeId, 'y', (seed % 11) * 30 + (Math.random() - 0.5) * jitter);
      });

      const selectedNodeIds = new Set();
      let hoveredNodeId = null;
      let isOverviewMode = false;
      let highlightedGroup = null;
      let baseCameraRatio = 1;
      const edgeEndpoints = new Map();
      [...edgesData, ...groupAttractionEdges].forEach((edge) => {
        edgeEndpoints.set(edge.id, { from: edge.from, to: edge.to, isAnchorEdge: !!edge.isAnchorEdge });
      });
      const shouldHideEdge = (attrs, endpoint) => {
        if (!endpoint || endpoint.isAnchorEdge) return true;
        if (isOverviewMode && overviewNodeHidingEnabled) {
          if (!overviewVisibleNodeIds.has(endpoint.from) || !overviewVisibleNodeIds.has(endpoint.to)) return true;
        }
        if (highlightedGroup !== null) {
          const sourceGroup = graph.getNodeAttribute(endpoint.from, 'group');
          const targetGroup = graph.getNodeAttribute(endpoint.to, 'group');
          if (!(sourceGroup === highlightedGroup || targetGroup === highlightedGroup)) return true;
        }
        return !!attrs.hidden;
      };

      const renderer = new Sigma(graph, container, {
        renderLabels: true,
        renderEdgeLabels: self.showEdgeLabels,
        labelFont: 'Arial',
        edgeLabelFont: 'Arial',
        labelSize: 12,
        edgeLabelSize: 10,
        defaultNodeType: 'border',
        defaultEdgeType: self.showArrows ? 'curvedArrow' : 'curved',
        nodeProgramClasses: {
          border: NodeBorderProgram,
        },
        edgeProgramClasses: {
          curved: EdgeCurveProgram,
          curvedArrow: EdgeCurvedArrowProgram,
          curvedCross: EdgeCurveProgram,
        },
        minEdgeThickness: EDGE_THICKNESS,
        hideEdgesOnMove: false,
        hideLabelsOnMove: false,
        enableEdgeEvents: true,
        labelRenderedSizeThreshold: 0,
        nodeReducer: (nodeKey, attrs) => {
          if (attrs.isAnchor) {
            return {
              ...attrs,
              x: attrs.x,
              y: attrs.y,
              hidden: true,
              label: '',
              color: 'rgba(0,0,0,0)',
            };
          }

          const reduced = {
            ...attrs,
            x: attrs.x,
            y: attrs.y,
            hidden: false,
            label: attrs.originalLabel || attrs.label || '',
            size: attrs.baseSize || attrs.size || 5,
            color: attrs.baseColor || attrs.color || '#64748b',
            borderColor: attrs.baseBorderColor || attrs.borderColor || self.darkenColor(attrs.baseColor || attrs.color || '#64748b', 48),
            forceLabel: false,
          };

          if (isOverviewMode && overviewNodeHidingEnabled && !overviewVisibleNodeIds.has(nodeKey)) {
            reduced.hidden = true;
            reduced.label = '';
            return reduced;
          }
          if (isOverviewMode) {
            reduced.label = '';
          }
          if (highlightedGroup !== null && attrs.group !== highlightedGroup) {
            reduced.color = '#cbd5e1';
            reduced.borderColor = '#94a3b8';
          }
          if (selectedNodeIds.has(nodeKey)) {
            reduced.size = (attrs.baseSize || 5) + 1.8;
            reduced.borderColor = attrs.baseBorderColor || self.darkenColor(attrs.baseColor || attrs.color || '#64748b', 62);
            reduced.forceLabel = true;
          }
          if (hoveredNodeId === nodeKey) {
            reduced.size = (attrs.baseSize || 5) + 1;
            reduced.borderColor = attrs.baseBorderColor || self.darkenColor(attrs.baseColor || attrs.color || '#64748b', 58);
            reduced.forceLabel = true;
          }
          return reduced;
        },
        edgeReducer: (edgeKey, attrs) => {
          const endpoint = edgeEndpoints.get(edgeKey);
          const isSelectedConnection = endpoint
            && !endpoint.isAnchorEdge
            && (selectedNodeIds.has(endpoint.from) || selectedNodeIds.has(endpoint.to));
          const hiddenByState = shouldHideEdge(attrs, endpoint);
          const reduced = {
            type: self.showArrows ? 'curvedArrow' : 'curved',
            label: self.showEdgeLabels ? (attrs.originalLabel || attrs.label || '') : '',
            size: isSelectedConnection ? SELECTED_EDGE_THICKNESS : (attrs.baseSize || 2.25),
            color: isSelectedConnection ? self.darkenColor(attrs.baseColor || attrs.color || '#95a5a6', 18) : (attrs.baseColor || attrs.color || '#95a5a6'),
            hidden: isSelectedConnection ? false : hiddenByState,
            curvature: attrs.curvature || 0.18,
            zIndex: isSelectedConnection ? 10 : 0,
          };
          if (attrs.isCrossCommunity && !attrs.isAnchorEdge && !isSelectedConnection) reduced.hidden = true;
          return reduced;
        },
      });

      self.network = renderer;
      self.graph = graph;
      self.nodes = graph;
      self.edges = graph;

      const getGraphPointFromSigmaEvent = (event) => {
        const original = event?.original;
        if (original && Number.isFinite(original.clientX) && Number.isFinite(original.clientY)) {
          const rect = container.getBoundingClientRect();
          return renderer.viewportToGraph({
            x: original.clientX - rect.left,
            y: original.clientY - rect.top,
          });
        }
        return renderer.viewportToGraph({ x: event?.x || 0, y: event?.y || 0 });
      };

      const runBarnesHutStep = (iterations, settings = {}, fixedNodesMap = null) => {
        const repulsion = settings.repulsion ?? 10000;
        const attraction = settings.attraction ?? 0.005;
        const gravity = settings.gravity ?? 0.04;
        const damping = settings.damping ?? 0.75;
        const edgeWeightInfluence = settings.edgeWeightInfluence ?? 0.85;
        const maxDisplacement = settings.maxDisplacement ?? 80;
        try {
          for (let iter = 0; iter < iterations; iter++) {
            const nodeList = [];
            const nodeIndex = new Map();
            graph.forEachNode((id, attrs) => {
              if (!Number.isFinite(attrs.x) || !Number.isFinite(attrs.y)) return;
              nodeIndex.set(id, nodeList.length);
              nodeList.push({ id, x: attrs.x, y: attrs.y, fx: 0, fy: 0 });
            });
            const n = nodeList.length;
            if (n < 2) break;

            // Repulsion between every pair
            for (let i = 0; i < n; i++) {
              for (let j = i + 1; j < n; j++) {
                const a = nodeList[i], b = nodeList[j];
                let dx = b.x - a.x, dy = b.y - a.y;
                let dist = Math.hypot(dx, dy);
                if (dist < 0.01) { dx = (Math.random() - 0.5) * 0.1; dy = (Math.random() - 0.5) * 0.1; dist = 0.01; }
                const force = repulsion / (dist * dist);
                const ux = dx / dist, uy = dy / dist;
                a.fx -= ux * force; a.fy -= uy * force;
                b.fx += ux * force; b.fy += uy * force;
              }
            }

            // Attraction along edges
            graph.forEachEdge((edgeId, attrs, src, tgt) => {
              if (attrs?.isAnchorEdge || attrs?.hidden) return;
              const si = nodeIndex.get(src), ti = nodeIndex.get(tgt);
              if (si === undefined || ti === undefined) return;
              const a = nodeList[si], b = nodeList[ti];
              const dx = b.x - a.x, dy = b.y - a.y;
              const dist = Math.hypot(dx, dy);
              if (dist < 0.001) return;
              const w = attrs.weight !== undefined ? Math.pow(Math.abs(attrs.weight), edgeWeightInfluence) : 1;
              const force = attraction * dist * w;
              const ux = dx / dist, uy = dy / dist;
              a.fx += ux * force; a.fy += uy * force;
              b.fx -= ux * force; b.fy -= uy * force;
            });

            // Gravity towards centroid
            let sumX = 0, sumY = 0;
            nodeList.forEach(nd => { sumX += nd.x; sumY += nd.y; });
            const cx = sumX / n, cy = sumY / n;
            nodeList.forEach(nd => {
              nd.fx -= gravity * (nd.x - cx);
              nd.fy -= gravity * (nd.y - cy);
            });

            // Apply forces with displacement cap
            nodeList.forEach(nd => {
              const len = Math.hypot(nd.fx, nd.fy);
              const scale = len > maxDisplacement ? maxDisplacement / len : 1;
              graph.setNodeAttribute(nd.id, 'x', nd.x + nd.fx * damping * scale);
              graph.setNodeAttribute(nd.id, 'y', nd.y + nd.fy * damping * scale);
            });

            if (fixedNodesMap) {
              fixedNodesMap.forEach((pos, nodeId) => {
                if (!graph.hasNode(nodeId)) return;
                graph.setNodeAttribute(nodeId, 'x', pos.x);
                graph.setNodeAttribute(nodeId, 'y', pos.y);
              });
            }
          }
        } catch (error) {
          console.warn('Barnes-Hut step failed:', error);
        }
      };

      const saveCurrentPositions = () => {
        if (!graph || !self.metaCommunityId) return;
        try {
          const serialized = {};
          graph.forEachNode((nodeId, attrs) => {
            if (attrs.isAnchor) return;
            const x = attrs.x;
            const y = attrs.y;
            if (Number.isFinite(x) && Number.isFinite(y)) {
              serialized[nodeId] = { x, y };
            }
          });

          let allCommunityPositions = [];
          try {
            const existingRaw = localStorage.getItem(positionStorageKey);
            const existingParsed = existingRaw ? JSON.parse(existingRaw) : [];
            allCommunityPositions = Array.isArray(existingParsed) ? existingParsed : [];
          } catch {
            allCommunityPositions = [];
          }

          const currentCommunityId = String(self.metaCommunityId);
          const filtered = allCommunityPositions.filter(item =>
            !(item && String(item.metaCommunityId) === currentCommunityId)
          );
          filtered.push({
            metaCommunityId: currentCommunityId,
            savedAt: new Date().toISOString(),
            positions: serialized,
          });

          localStorage.setItem(positionStorageKey, JSON.stringify(filtered));
        } catch (error) {
          console.warn('Failed to save graph positions:', error);
        }
      };

      const fitGraph = (animate = true) => {
        const camera = renderer.getCamera();
        if (animate) {
          camera.animatedReset({ duration: 500 }).then(() => {
            baseCameraRatio = renderer.getCamera().getState().ratio || 1;
          }).catch(() => {});
        } else {
          camera.setState({ x: 0.5, y: 0.5, ratio: 1, angle: 0 });
          baseCameraRatio = renderer.getCamera().getState().ratio || 1;
        }
      };

      const LABEL_VISIBLE_SCALE = 2;
      const applyZoomRenderMode = () => {
        const ratio = renderer.getCamera().getState().ratio;
        const pseudoScale = (baseCameraRatio > 0 && Number.isFinite(baseCameraRatio)) ? (baseCameraRatio / ratio) : 1;
        const shouldUseOverview = pseudoScale < LABEL_VISIBLE_SCALE;
        if (shouldUseOverview === isOverviewMode) return;
        isOverviewMode = shouldUseOverview;
        renderer.refresh();
      };
      const throttledApplyZoomRenderMode = throttle(applyZoomRenderMode, 33);

      const tooltipEl = document.createElement('div');
      tooltipEl.className = 'sigma-tooltip';
      tooltipEl.style.display = 'none';
      container.appendChild(tooltipEl);
      const hideTooltip = () => {
        if (self._tooltipTimer) {
          clearTimeout(self._tooltipTimer);
          self._tooltipTimer = null;
        }
        tooltipEl.style.display = 'none';
      };
      const showTooltip = (nodeKey) => {
        const attrs = graph.getNodeAttributes(nodeKey);
        if (!attrs || attrs.isAnchor) return;
        const displayData = renderer.getNodeDisplayData(nodeKey);
        if (!displayData) return;
        const pt = renderer.graphToViewport({ x: displayData.x, y: displayData.y });
        tooltipEl.innerHTML = attrs.tooltipHtml || '';
        tooltipEl.style.left = `${pt.x + 10}px`;
        tooltipEl.style.top = `${pt.y + 10}px`;
        tooltipEl.style.display = 'block';
      };

      const crossCommunityEdgeIds = edgesData.filter(edge => edge.isCrossCommunity).map(edge => edge.id);
      const crossEdgeCanvas = document.createElement('canvas');
      crossEdgeCanvas.className = 'sigma-cross-edges';
      container.appendChild(crossEdgeCanvas);
      const crossEdgeCtx = crossEdgeCanvas.getContext('2d');
      const syncCrossEdgeCanvasSize = () => {
        const ratio = window.devicePixelRatio || 1;
        const width = container.clientWidth;
        const height = container.clientHeight;
        crossEdgeCanvas.width = Math.max(1, Math.floor(width * ratio));
        crossEdgeCanvas.height = Math.max(1, Math.floor(height * ratio));
        crossEdgeCanvas.style.width = `${width}px`;
        crossEdgeCanvas.style.height = `${height}px`;
        if (crossEdgeCtx) crossEdgeCtx.setTransform(ratio, 0, 0, ratio, 0, 0);
      };
      const drawCrossCommunityEdges = () => {
        if (!crossEdgeCtx) return;
        const width = container.clientWidth;
        const height = container.clientHeight;
        crossEdgeCtx.clearRect(0, 0, width, height);
        crossEdgeCtx.save();
        crossEdgeCtx.globalAlpha = 0.85;

        crossCommunityEdgeIds.forEach((edgeId) => {
          if (!graph.hasEdge(edgeId)) return;
          const attrs = graph.getEdgeAttributes(edgeId);
          const endpoint = edgeEndpoints.get(edgeId);
          if (!endpoint || shouldHideEdge(attrs, endpoint)) return;
          if (!graph.hasNode(endpoint.from) || !graph.hasNode(endpoint.to)) return;

          const source = graph.getNodeAttributes(endpoint.from);
          const target = graph.getNodeAttributes(endpoint.to);
          const p0 = renderer.graphToViewport({ x: source.x, y: source.y });
          const p2 = renderer.graphToViewport({ x: target.x, y: target.y });
          const dx = p2.x - p0.x;
          const dy = p2.y - p0.y;
          const len = Math.hypot(dx, dy);
          if (!Number.isFinite(len) || len < 1) return;
          const nx = -dy / len;
          const ny = dx / len;
          const curvature = Number.isFinite(attrs.curvature) ? attrs.curvature : 0.3;
          const offset = len * curvature * 0.5;
          const cp = {
            x: (p0.x + p2.x) * 0.5 + nx * offset,
            y: (p0.y + p2.y) * 0.5 + ny * offset,
          };

          const dottedColor = self.darkenColor(attrs.baseColor || attrs.color || '#95a5a6', 35);
          crossEdgeCtx.beginPath();
          crossEdgeCtx.moveTo(p0.x, p0.y);
          crossEdgeCtx.quadraticCurveTo(cp.x, cp.y, p2.x, p2.y);
          crossEdgeCtx.strokeStyle = dottedColor;
          crossEdgeCtx.lineWidth = Math.max(0.75, (attrs.baseSize || attrs.size || CROSS_EDGE_THICKNESS) * 0.9);
          crossEdgeCtx.setLineDash([2, 4]);
          crossEdgeCtx.stroke();

          if (self.showArrows) {
            // Arrowhead tangent near t=1 for directional hint on dotted cross edges.
            const t = 0.96;
            const tx = 2 * (1 - t) * (cp.x - p0.x) + 2 * t * (p2.x - cp.x);
            const ty = 2 * (1 - t) * (cp.y - p0.y) + 2 * t * (p2.y - cp.y);
            const tLen = Math.hypot(tx, ty) || 1;
            const ux = tx / tLen;
            const uy = ty / tLen;
            const arrow = 6;
            const side = 3.25;
            crossEdgeCtx.setLineDash([]);
            crossEdgeCtx.beginPath();
            crossEdgeCtx.moveTo(p2.x, p2.y);
            crossEdgeCtx.lineTo(p2.x - ux * arrow + -uy * side, p2.y - uy * arrow + ux * side);
            crossEdgeCtx.lineTo(p2.x - ux * arrow - -uy * side, p2.y - uy * arrow - ux * side);
            crossEdgeCtx.closePath();
            crossEdgeCtx.fillStyle = dottedColor;
            crossEdgeCtx.fill();
          }
        });

        crossEdgeCtx.restore();
      };
      syncCrossEdgeCanvasSize();

      const getNodeClusterKey = (attrs) => attrs.colorGroup ?? attrs.group ?? 0;

      const placeStandaloneNodesOnPerimeter = () => {
        const groups = new Map();
        const standaloneGroups = new Map();
        const connectedGroups = new Map();
        const coreNodes = [];
        graph.forEachNode((nodeId, attrs) => {
          if (attrs.isAnchor) return;
          const degree = nodeDegrees[nodeId] || 0;
          if (!Number.isFinite(attrs.x) || !Number.isFinite(attrs.y)) return;
          const clusterKey = getNodeClusterKey(attrs);
          if (!groups.has(clusterKey)) groups.set(clusterKey, []);
          groups.get(clusterKey).push({ id: nodeId, x: attrs.x, y: attrs.y, size: attrs.baseSize || attrs.size || 8 });
          const targetGroups = degree === 0 ? standaloneGroups : connectedGroups;
          if (!targetGroups.has(clusterKey)) targetGroups.set(clusterKey, []);
          targetGroups.get(clusterKey).push({ id: nodeId, x: attrs.x, y: attrs.y, size: attrs.baseSize || attrs.size || 8 });
          if (degree !== 0) coreNodes.push({ id: nodeId, x: attrs.x, y: attrs.y, group: clusterKey });
        });
        if (standaloneGroups.size === 0 || coreNodes.length === 0) return;

        const cx = coreNodes.reduce((sum, n) => sum + n.x, 0) / coreNodes.length;
        const cy = coreNodes.reduce((sum, n) => sum + n.y, 0) / coreNodes.length;
        let maxCoreRadius = 0;
        coreNodes.forEach((node) => {
          const r = Math.hypot(node.x - cx, node.y - cy);
          if (r > maxCoreRadius) maxCoreRadius = r;
        });

        const groupEntries = [...standaloneGroups.entries()].sort((a, b) => a[0] - b[0]);
        const baseRadius = Math.max(maxCoreRadius + 200, 460);
        const groupStep = (Math.PI * 2) / groupEntries.length;
        groupEntries.forEach(([group, nodes], groupIndex) => {
          const connected = connectedGroups.get(group) || [];
          let groupCx = cx;
          let groupCy = cy;
          if (connected.length > 0) {
            groupCx = connected.reduce((sum, n) => sum + n.x, 0) / connected.length;
            groupCy = connected.reduce((sum, n) => sum + n.y, 0) / connected.length;
          }
          const direction = Math.atan2(groupCy - cy, groupCx - cx);
          const angle = Number.isFinite(direction) && Math.hypot(groupCx - cx, groupCy - cy) > 0.001
            ? direction
            : (groupIndex * groupStep) - (Math.PI / 2);
          const clusterCx = cx + Math.cos(angle) * baseRadius;
          const clusterCy = cy + Math.sin(angle) * baseRadius;
          const localGap = 38;
          const localRadiusStep = 48;
          nodes.forEach((node, index) => {
            const localRing = Math.floor((Math.sqrt(index + 1) - 1) * 1.7);
            const itemsInRing = Math.max(1, Math.ceil((localRing + 1) * 6));
            const localIndex = index % itemsInRing;
            const localAngle = ((Math.PI * 2) * localIndex / itemsInRing) + (localRing * 0.31);
            const localRadius = index === 0 ? 0 : Math.max(localGap, localRing * localRadiusStep);
            graph.setNodeAttribute(node.id, 'x', clusterCx + Math.cos(localAngle) * localRadius);
            graph.setNodeAttribute(node.id, 'y', clusterCy + Math.sin(localAngle) * localRadius);
          });
        });
      };

      const applyCommunityCohesion = (strength = 0.3, passes = 1) => {
        if (!graph) return;
        for (let pass = 0; pass < passes; pass++) {
          const groupStats = new Map();
          graph.forEachNode((nodeId, attrs) => {
            if (attrs.isAnchor) return;
            if (!Number.isFinite(attrs.x) || !Number.isFinite(attrs.y)) return;
            const key = getNodeClusterKey(attrs);
            if (!groupStats.has(key)) {
              groupStats.set(key, { sumX: 0, sumY: 0, count: 0 });
            }
            const stat = groupStats.get(key);
            stat.sumX += attrs.x;
            stat.sumY += attrs.y;
            stat.count += 1;
          });

          graph.forEachNode((nodeId, attrs) => {
            if (attrs.isAnchor) return;
            const stat = groupStats.get(getNodeClusterKey(attrs));
            if (!stat || stat.count <= 1) return;
            const cx = stat.sumX / stat.count;
            const cy = stat.sumY / stat.count;
            graph.setNodeAttribute(nodeId, 'x', attrs.x + (cx - attrs.x) * strength);
            graph.setNodeAttribute(nodeId, 'y', attrs.y + (cy - attrs.y) * strength);
          });
        }
      };

      const arrangeCommunityClusters = (passes = 8) => {
        if (!graph) return;
        const groups = new Map();
        graph.forEachNode((nodeId, attrs) => {
          if (attrs.isAnchor) return;
          if ((nodeDegrees[nodeId] || 0) === 0) return;
          if (!Number.isFinite(attrs.x) || !Number.isFinite(attrs.y)) return;
          const clusterKey = getNodeClusterKey(attrs);
          if (!groups.has(clusterKey)) groups.set(clusterKey, []);
          groups.get(clusterKey).push({ id: nodeId, x: attrs.x, y: attrs.y });
        });
        if (groups.size <= 1) return;

        const clusterList = [...groups.entries()].map(([group, nodes]) => {
          const cx = nodes.reduce((sum, n) => sum + n.x, 0) / nodes.length;
          const cy = nodes.reduce((sum, n) => sum + n.y, 0) / nodes.length;
          return { group, nodes, cx, cy, count: nodes.length };
        }).sort((a, b) => b.count - a.count);

        const clusterCount = clusterList.length;
        const ringCount = Math.max(1, Math.ceil(clusterCount / 24));
        const baseRadius = Math.max(360, Math.sqrt(clusterCount) * 105);
        const ringGap = 360;

        clusterList.forEach((cluster, index) => {
          const ringIndex = Math.floor(index / 24);
          const indexInRing = index % 24;
          const itemsInRing = Math.min(24, clusterCount - (ringIndex * 24));
          const angle = ((Math.PI * 2) * indexInRing / itemsInRing) - (Math.PI / 2) + (ringIndex * 0.19);
          const radius = baseRadius + (Math.min(ringIndex, ringCount - 1) * ringGap);
          cluster.targetX = Math.cos(angle) * radius;
          cluster.targetY = Math.sin(angle) * radius;
        });

        for (let pass = 0; pass < passes; pass++) {
          const strength = pass < passes - 2 ? 0.34 : 0.18;
          clusterList.forEach((cluster) => {
            const current = cluster.nodes.reduce((acc, node) => {
              const attrs = graph.getNodeAttributes(node.id);
              acc.x += attrs.x;
              acc.y += attrs.y;
              return acc;
            }, { x: 0, y: 0 });
            const currentCx = current.x / cluster.nodes.length;
            const currentCy = current.y / cluster.nodes.length;
            const dx = (cluster.targetX - currentCx) * strength;
            const dy = (cluster.targetY - currentCy) * strength;
            cluster.nodes.forEach((node) => {
              const attrs = graph.getNodeAttributes(node.id);
              graph.setNodeAttribute(node.id, 'x', attrs.x + dx);
              graph.setNodeAttribute(node.id, 'y', attrs.y + dy);
            });
          });
          applyCommunityCohesion(0.28, 1);
          resolveNodeOverlaps(2);
        }
      };

      const enforceConnectedNodeSpacing = (extraPaddingPx = 20, passes = 5) => {
        if (!graph || !renderer) return;
        const ratio = Math.max(0.0001, renderer.getGraphToViewportRatio ? renderer.getGraphToViewportRatio() : 1);

        for (let pass = 0; pass < passes; pass++) {
          let moved = false;
          graph.forEachEdge((edgeKey, edgeAttrs, source, target, sourceAttrs, targetAttrs) => {
            if (edgeAttrs?.isAnchorEdge) return;
            if (!sourceAttrs || !targetAttrs) return;
            if (sourceAttrs.isAnchor || targetAttrs.isAnchor) return;
            if (!Number.isFinite(sourceAttrs.x) || !Number.isFinite(sourceAttrs.y)) return;
            if (!Number.isFinite(targetAttrs.x) || !Number.isFinite(targetAttrs.y)) return;

            const dx = targetAttrs.x - sourceAttrs.x;
            const dy = targetAttrs.y - sourceAttrs.y;
            const dist = Math.hypot(dx, dy);
            const sourceSize = sourceAttrs.baseSize || sourceAttrs.size || 8;
            const targetSize = targetAttrs.baseSize || targetAttrs.size || 8;
            const minDistGraph = (sourceSize + targetSize + extraPaddingPx) / ratio;
            if (dist >= minDistGraph) return;

            const overlap = minDistGraph - dist;
            const ux = dist > 0.00001 ? dx / dist : (Math.random() - 0.5);
            const uy = dist > 0.00001 ? dy / dist : (Math.random() - 0.5);
            const push = overlap * 0.5;
            graph.setNodeAttribute(source, 'x', sourceAttrs.x - ux * push);
            graph.setNodeAttribute(source, 'y', sourceAttrs.y - uy * push);
            graph.setNodeAttribute(target, 'x', targetAttrs.x + ux * push);
            graph.setNodeAttribute(target, 'y', targetAttrs.y + uy * push);
            moved = true;
          });
          if (!moved) break;
        }
      };

      const resolveNodeOverlaps = (passes = 6) => {
        if (!graph || !renderer) return;
        const nodes = [];
        graph.forEachNode((nodeId, attrs) => {
          if (attrs.isAnchor) return;
          if (!Number.isFinite(attrs.x) || !Number.isFinite(attrs.y)) return;
          nodes.push({
            id: nodeId,
            x: attrs.x,
            y: attrs.y,
            size: attrs.baseSize || attrs.size || 8,
            group: getNodeClusterKey(attrs),
          });
        });
        if (nodes.length < 2) return;

        const graphToViewportRatio = Math.max(0.0001, renderer.getGraphToViewportRatio ? renderer.getGraphToViewportRatio() : 1);
        const paddingPx = TARGET_NODE_GAP_PX;

        for (let pass = 0; pass < passes; pass++) {
          let moved = false;
          for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
              const a = nodes[i];
              const b = nodes[j];
              const dx = b.x - a.x;
              const dy = b.y - a.y;
              const dist = Math.hypot(dx, dy);
              const pairPaddingPx = a.group === b.group ? paddingPx * 0.6 : paddingPx * 1.4;
              const minDist = (a.size + b.size + pairPaddingPx) / graphToViewportRatio;
              if (dist >= minDist) continue;

              const angle = dist > 0.00001 ? Math.atan2(dy, dx) : Math.random() * Math.PI * 2;
              const ux = Math.cos(angle);
              const uy = Math.sin(angle);
              const push = (minDist - dist) * 0.55;
              a.x -= ux * push;
              a.y -= uy * push;
              b.x += ux * push;
              b.y += uy * push;
              moved = true;
            }
          }
          if (!moved) break;
        }

        nodes.forEach((node) => {
          graph.setNodeAttribute(node.id, 'x', node.x);
          graph.setNodeAttribute(node.id, 'y', node.y);
        });
      };

      const spreadNodesApart = (minGapPx = TARGET_NODE_GAP_PX, passes = 12) => {
        resolveNodeOverlaps(passes);
        if (!graph || !renderer) return;
        const graphToViewportRatio = Math.max(0.0001, renderer.getGraphToViewportRatio ? renderer.getGraphToViewportRatio() : 1);
        const minExtraGraph = minGapPx / graphToViewportRatio;

        for (let pass = 0; pass < passes; pass++) {
          const groupStats = new Map();
          graph.forEachNode((nodeId, attrs) => {
            if (attrs.isAnchor) return;
            if (!Number.isFinite(attrs.x) || !Number.isFinite(attrs.y)) return;
            const clusterKey = getNodeClusterKey(attrs);
            if (!groupStats.has(clusterKey)) groupStats.set(clusterKey, { x: 0, y: 0, count: 0 });
            const stat = groupStats.get(clusterKey);
            stat.x += attrs.x;
            stat.y += attrs.y;
            stat.count += 1;
          });

          graph.forEachNode((nodeId, attrs) => {
            if (attrs.isAnchor) return;
            const stat = groupStats.get(getNodeClusterKey(attrs));
            if (!stat || stat.count <= 1) return;
            const cx = stat.x / stat.count;
            const cy = stat.y / stat.count;
            const dx = attrs.x - cx;
            const dy = attrs.y - cy;
            const dist = Math.hypot(dx, dy);
            if (dist >= minExtraGraph) return;
            const angle = dist > 0.00001 ? Math.atan2(dy, dx) : Math.random() * Math.PI * 2;
            const push = (minExtraGraph - dist) * 0.25;
            graph.setNodeAttribute(nodeId, 'x', attrs.x + Math.cos(angle) * push);
            graph.setNodeAttribute(nodeId, 'y', attrs.y + Math.sin(angle) * push);
          });
        }
      };

      const compactCommunityGroups = (strength = 0.34, passes = 4) => {
        if (!graph || !renderer) return;
        for (let pass = 0; pass < passes; pass++) {
          applyCommunityCohesion(strength, 1);
          resolveNodeOverlaps(2);
        }
      };

      const separateColorGroups = (minGap = 250, passes = 8) => {
        if (!graph) return;
        for (let pass = 0; pass < passes; pass++) {
          const clusters = new Map();
          graph.forEachNode((nodeId, attrs) => {
            if (attrs.isAnchor) return;
            if (!Number.isFinite(attrs.x) || !Number.isFinite(attrs.y)) return;
            const key = getNodeClusterKey(attrs);
            if (!clusters.has(key)) clusters.set(key, { nodes: [], x: 0, y: 0 });
            const cluster = clusters.get(key);
            cluster.nodes.push(nodeId);
            cluster.x += attrs.x;
            cluster.y += attrs.y;
          });

          const clusterList = [...clusters.values()]
            .filter((cluster) => cluster.nodes.length > 0)
            .map((cluster) => ({
              ...cluster,
              x: cluster.x / cluster.nodes.length,
              y: cluster.y / cluster.nodes.length,
            }));
          if (clusterList.length < 2) return;

          for (let i = 0; i < clusterList.length; i++) {
            for (let j = i + 1; j < clusterList.length; j++) {
              const a = clusterList[i];
              const b = clusterList[j];
              const dx = b.x - a.x;
              const dy = b.y - a.y;
              const dist = Math.hypot(dx, dy) || 0.0001;
              const requiredGap = minGap + Math.sqrt(a.nodes.length + b.nodes.length) * 18;
              if (dist >= requiredGap) continue;
              const ux = dx / dist;
              const uy = dy / dist;
              const push = (requiredGap - dist) * 0.45;
              a.nodes.forEach((nodeId) => {
                const attrs = graph.getNodeAttributes(nodeId);
                graph.setNodeAttribute(nodeId, 'x', attrs.x - ux * push);
                graph.setNodeAttribute(nodeId, 'y', attrs.y - uy * push);
              });
              b.nodes.forEach((nodeId) => {
                const attrs = graph.getNodeAttributes(nodeId);
                graph.setNodeAttribute(nodeId, 'x', attrs.x + ux * push);
                graph.setNodeAttribute(nodeId, 'y', attrs.y + uy * push);
              });
            }
          }
        }
      };

      const runFinalSpacingPass = () => {
        arrangeCommunityClusters(12);
        separateColorGroups(280, 8);
        compactCommunityGroups(0.48, 6);
        separateColorGroups(260, 4);
        enforceConnectedNodeSpacing(18, 3);
        resolveNodeOverlaps(8);
        compactCommunityGroups(0.22, 2);
        separateColorGroups(240, 3);
        resolveNodeOverlaps(10);
        compactCommunityGroups(0.35, 4);
        placeStandaloneNodesOnPerimeter();
        resolveNodeOverlaps(6);
        compactCommunityGroups(0.2, 2);
      };

      const activeDragNodeIds = new Set();
      const dragNodeStartPositions = new Map();
      const dragNodeVelocities = new Map();
      const dragNeighborDepth = new Map();
      const fixedNodePositions = new Map();
      let dragFrameRequested = false;
      let isDraggingNode = false;
      let draggedNodeId = null;
      let dragStartPoint = null;
      let hasDraggedNode = false;
      let latestDragPoint = null;
      const dragMoveThreshold = 3;

      const finishDragInteraction = () => {
        dragFrameRequested = false;
        isDraggingNode = false;
        renderer.setSetting('enableCameraPanning', true);
        fixedNodePositions.clear();
        activeDragNodeIds.clear();
        dragNodeStartPositions.clear();
        dragNodeVelocities.clear();
        dragNeighborDepth.clear();
        draggedNodeId = null;
        dragStartPoint = null;
        latestDragPoint = null;
        saveCurrentPositions();
        // self.physicsEnabled = false;
      };

      const navEl = document.createElement('div');
      navEl.className = 'sigma-nav';
      navEl.innerHTML = `
        <button type="button" data-nav="in">+</button>
        <button type="button" data-nav="out">-</button>
        <button type="button" data-nav="fit">&#9633;</button>
      `;
      container.appendChild(navEl);
      navEl.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        const navType = button.getAttribute('data-nav');
        const camera = renderer.getCamera();
        if (navType === 'in') camera.animatedZoom({ factor: 1.5, duration: 120 }).catch(() => {});
        if (navType === 'out') camera.animatedUnzoom({ factor: 1.5, duration: 120 }).catch(() => {});
        if (navType === 'fit') fitGraph(true);
      });

      container.setAttribute('tabindex', '0');
      const containerKeydownHandler = (event) => {
        const camera = renderer.getCamera();
        const state = camera.getState();
        const panStep = 0.05 * state.ratio;
        if (event.key === 'ArrowUp') { event.preventDefault(); camera.setState({ y: state.y - panStep }); }
        if (event.key === 'ArrowDown') { event.preventDefault(); camera.setState({ y: state.y + panStep }); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); camera.setState({ x: state.x - panStep }); }
        if (event.key === 'ArrowRight') { event.preventDefault(); camera.setState({ x: state.x + panStep }); }
        if (event.key === '+' || event.key === '=') { event.preventDefault(); camera.animatedZoom({ factor: 1.3, duration: 120 }).catch(() => {}); }
        if (event.key === '-') { event.preventDefault(); camera.animatedUnzoom({ factor: 1.3, duration: 120 }).catch(() => {}); }
        if (event.key === '0') { event.preventDefault(); fitGraph(true); }
      };
      container.addEventListener('keydown', containerKeydownHandler);

      self._sigmaState = {
        selectedNodeIds,
        nodeNeighborMap,
        overviewVisibleNodeIds,
        tooltipEl,
        navEl,
        crossEdgeCanvas,
        containerKeydownHandler,
        setHighlightedGroup: (groupIndex) => {
          highlightedGroup = groupIndex;
          renderer.refresh();
        },
        clearHighlightedGroup: () => {
          highlightedGroup = null;
          renderer.refresh();
        },
        saveCurrentPositions,
        fitGraph,
      };

      renderer.on('clickNode', (payload) => {
        hideTooltip();
        if (hasDraggedNode) {
          hasDraggedNode = false;
          return;
        }
        const nodeId = payload.node;
        const attrs = graph.getNodeAttributes(nodeId);
        if (!attrs || attrs.isAnchor) return;

        const original = payload.event?.original;
        const isMulti = !!(original && (original.ctrlKey || original.metaKey));
        if (!isMulti) selectedNodeIds.clear();
        selectedNodeIds.add(nodeId);

        const node = graph.getNodeAttributes(nodeId);
        self.selectedNode = {
          ...node,
          id: node.originalId ?? nodeId,
          label: node.originalLabel || node.label,
          title: node.fullContent,
        };
        self.sourceChunks = [];
        self.selectedChunkIndex = 0;
        if (node.entityId) {
          self.fetchSourceChunks();
        }
        renderer.refresh();
      });

      renderer.on('clickStage', () => {
        hideTooltip();
        selectedNodeIds.clear();
        self.selectedNode = null;
        self.sourceChunks = [];
        self.selectedChunkIndex = 0;
        renderer.refresh();
      });

      renderer.on('enterNode', (payload) => {
        hoveredNodeId = payload.node;
        if (self._tooltipTimer) clearTimeout(self._tooltipTimer);
        self._tooltipTimer = setTimeout(() => {
          showTooltip(payload.node);
        }, 100);
        renderer.refresh();
      });

      renderer.on('leaveNode', () => {
        hoveredNodeId = null;
        hideTooltip();
        renderer.refresh();
      });

      renderer.on('downNode', (payload) => {
        hideTooltip();
        if (self._dragEndTimer) {
          clearTimeout(self._dragEndTimer);
          self._dragEndTimer = null;
        }
        if (self._dragStabilizeTimer) {
          clearTimeout(self._dragStabilizeTimer);
          self._dragStabilizeTimer = null;
        }

        isDraggingNode = true;
        draggedNodeId = payload.node;
        dragStartPoint = getGraphPointFromSigmaEvent(payload.event);
        latestDragPoint = dragStartPoint;
        hasDraggedNode = false;
        activeDragNodeIds.clear();
        activeDragNodeIds.add(draggedNodeId);
        dragNeighborDepth.clear();
        dragNeighborDepth.set(draggedNodeId, 0);
        const neighbors = nodeNeighborMap.get(draggedNodeId) || new Set();
        neighbors.forEach((neighbor) => {
          activeDragNodeIds.add(neighbor);
          dragNeighborDepth.set(neighbor, 1);
        });
        neighbors.forEach((neighbor) => {
          const secondHopNeighbors = nodeNeighborMap.get(neighbor) || new Set();
          secondHopNeighbors.forEach((secondHopNode) => {
            if (secondHopNode === draggedNodeId || activeDragNodeIds.has(secondHopNode)) return;
            activeDragNodeIds.add(secondHopNode);
            dragNeighborDepth.set(secondHopNode, 2);
          });
        });

        dragNodeStartPositions.clear();
        dragNodeVelocities.clear();
        activeDragNodeIds.forEach((nodeId) => {
          if (!graph.hasNode(nodeId)) return;
          const attrs = graph.getNodeAttributes(nodeId);
          if (!Number.isFinite(attrs.x) || !Number.isFinite(attrs.y)) return;
          dragNodeStartPositions.set(nodeId, { x: attrs.x, y: attrs.y });
          dragNodeVelocities.set(nodeId, { x: 0, y: 0 });
        });

        fixedNodePositions.clear();
        graph.forEachNode((nodeId, attrs) => {
          if (attrs.isAnchor) return;
          if (!activeDragNodeIds.has(nodeId)) {
            fixedNodePositions.set(nodeId, { x: attrs.x, y: attrs.y });
          }
        });

        renderer.setSetting('enableCameraPanning', false);
        self.physicsEnabled = false;
      });

      const scheduleDragPhysicsFrame = () => {
        if (dragFrameRequested) return;
        dragFrameRequested = true;
        requestAnimationFrame(() => {
          dragFrameRequested = false;
          if (!isDraggingNode || !latestDragPoint || !draggedNodeId || !graph.hasNode(draggedNodeId)) return;

          const dx = dragStartPoint ? latestDragPoint.x - dragStartPoint.x : 0;
          const dy = dragStartPoint ? latestDragPoint.y - dragStartPoint.y : 0;
          let maxVelocity = 0;
          graph.setNodeAttribute(draggedNodeId, 'x', latestDragPoint.x);
          graph.setNodeAttribute(draggedNodeId, 'y', latestDragPoint.y);

          dragNodeStartPositions.forEach((startPos, nodeId) => {
            if (nodeId === draggedNodeId || !graph.hasNode(nodeId)) return;
            const depth = dragNeighborDepth.get(nodeId) || 1;
            const attrs = graph.getNodeAttributes(nodeId);
            const velocity = dragNodeVelocities.get(nodeId) || { x: 0, y: 0 };
            const targetFollow = depth === 1 ? 0.86 : 0.48;
            const spring = depth === 1 ? 0.28 : 0.12;
            const damping = depth === 1 ? 0.68 : 0.74;
            const targetX = startPos.x + dx * targetFollow;
            const targetY = startPos.y + dy * targetFollow;
            velocity.x = (velocity.x + (targetX - attrs.x) * spring) * damping;
            velocity.y = (velocity.y + (targetY - attrs.y) * spring) * damping;

            const neighbors = nodeNeighborMap.get(nodeId) || new Set();
            let pullX = 0;
            let pullY = 0;
            let pullCount = 0;
            neighbors.forEach((neighborId) => {
              if (!activeDragNodeIds.has(neighborId) || !graph.hasNode(neighborId)) return;
              const neighborAttrs = graph.getNodeAttributes(neighborId);
              pullX += neighborAttrs.x - attrs.x;
              pullY += neighborAttrs.y - attrs.y;
              pullCount++;
            });
            if (pullCount > 0) {
              velocity.x += (pullX / pullCount) * (depth === 1 ? 0.018 : 0.01);
              velocity.y += (pullY / pullCount) * (depth === 1 ? 0.018 : 0.01);
            }

            maxVelocity = Math.max(maxVelocity, Math.hypot(velocity.x, velocity.y));
            graph.setNodeAttribute(nodeId, 'x', attrs.x + velocity.x);
            graph.setNodeAttribute(nodeId, 'y', attrs.y + velocity.y);
            dragNodeVelocities.set(nodeId, velocity);
          });

          renderer.refresh();
          if (isDraggingNode && maxVelocity > 0.015) scheduleDragPhysicsFrame();
        });
      };

      renderer.on('moveBody', (payload) => {
        if (!isDraggingNode || !draggedNodeId || !graph.hasNode(draggedNodeId)) return;
        latestDragPoint = getGraphPointFromSigmaEvent(payload.event);
        payload.event?.preventSigmaDefault?.();
        payload.event?.original?.preventDefault?.();
        payload.event?.original?.stopPropagation?.();
        if (dragStartPoint && Math.hypot(latestDragPoint.x - dragStartPoint.x, latestDragPoint.y - dragStartPoint.y) > dragMoveThreshold) {
          hasDraggedNode = true;
        }
        scheduleDragPhysicsFrame();
      });

      const handleDragEnd = () => {
        if (!draggedNodeId) return;
        isDraggingNode = false;
        finishDragInteraction();
      };
      renderer.on('upNode', handleDragEnd);
      renderer.on('upStage', handleDragEnd);

      renderer.getCamera().on('updated', throttledApplyZoomRenderMode);
      renderer.on('afterRender', drawCrossCommunityEdges);
      renderer.on('resize', () => {
        syncCrossEdgeCanvasSize();
        drawCrossCommunityEdges();
      });
      drawCrossCommunityEdges();

      const initialPhysicsSettings = {
        repulsion: 38000,
        attraction: 0.0018,
        gravity: 0.012,
        damping: 0.68,
        edgeWeightInfluence: 0.6,
        maxDisplacement: 150,
      };

      if (!hasCompleteStoredPositions) {
        randomLayout.assign(graph, { scale: 720 });

        // Pre-seed standalone nodes far from the core so layout starts them outside.
        {
          const standaloneIds = [];
          const connectedPositions = [];
          graph.forEachNode((nodeId, attrs) => {
            if (attrs.isAnchor) return;
            const deg = nodeDegrees[nodeId] || 0;
            if (deg === 0) standaloneIds.push(nodeId);
            else connectedPositions.push({ x: attrs.x, y: attrs.y });
          });
          if (standaloneIds.length > 0) {
            const cx = connectedPositions.length
              ? connectedPositions.reduce((s, n) => s + n.x, 0) / connectedPositions.length
              : 0;
            const cy = connectedPositions.length
              ? connectedPositions.reduce((s, n) => s + n.y, 0) / connectedPositions.length
              : 0;
            const seedRadius = 620;
            const step = (Math.PI * 2) / standaloneIds.length;
            standaloneIds.forEach((nodeId, i) => {
              const angle = i * step - Math.PI / 2;
              graph.setNodeAttribute(nodeId, 'x', cx + Math.cos(angle) * seedRadius);
              graph.setNodeAttribute(nodeId, 'y', cy + Math.sin(angle) * seedRadius);
            });
          }
        }

        self.physicsEnabled = true;

        let ticks = 0;
        const totalIterations = 300;
        const batchIterations = 25;
        const totalTicks = Math.ceil(totalIterations / batchIterations);
        const stabilizeStep = () => {
          if (!self.network || self.network !== renderer) return;
          if (ticks >= totalTicks) {
            runBarnesHutStep(40, {
              ...initialPhysicsSettings,
              gravity: 0.008,
              repulsion: 28000,
              edgeWeightInfluence: 0.55,
            });
            runFinalSpacingPass();
            // self.physicsEnabled = false;
            saveCurrentPositions();
            fitGraph(true);
            applyZoomRenderMode();
            renderer.refresh();
            return;
          }
          runBarnesHutStep(batchIterations, initialPhysicsSettings);
          applyCommunityCohesion(0.20, 1);
          separateColorGroups(180, 2);
          resolveNodeOverlaps(2);
          renderer.scheduleRefresh();
          ticks++;
          self._stabilizeTimer = setTimeout(stabilizeStep, 50);
        };
        stabilizeStep();
      } else {
        runBarnesHutStep(20, {
          ...initialPhysicsSettings,
          gravity: 0.006,
          repulsion: 22000,
          edgeWeightInfluence: 0.55,
        });
        runFinalSpacingPass();
        // self.physicsEnabled = false;
        fitGraph(false);
        applyZoomRenderMode();
        renderer.refresh();
      }
    },
    buildNodeTooltip(node, group) {
      // Use entityName for entity-based, label for chunk-based
      const displayName = node.entityName || node.label || `Node ${node.id}`;
      const entityType = node.entityType || 'chunk';

      const lines = [
        `<b>${this.escapeHtml(displayName)}</b>`,
        `Type: ${entityType}`,
      ];

      // Entity-based: show description
      if (node.content) {
        lines.push(`<hr>`);
        lines.push(`${this.escapeHtml(this.truncateSummary(node.content, 150))}`);
      }

      // Show sources if available
      if (node.sources && node.sources.length > 0) {
        lines.push(`<hr>`);
        lines.push(`Sources: ${node.sources.slice(0, 3).join(', ')}${node.sources.length > 3 ? '...' : ''}`);
      }

      lines.push(`<hr>`);
      lines.push(`<b>Community ${node.group + 1}</b>`);
      lines.push(`Members: ${group.memberCount || 'N/A'}`);

      if (group.summary) {
        lines.push(`Summary: ${this.escapeHtml(this.truncateSummary(group.summary, 80))}`);
      }

      // Chunk-based: show content
      if (!node.content && node.title) {
        lines.push(`<hr>`);
        lines.push(`Content: ${this.escapeHtml(this.truncateSummary(node.title, 150))}`);
      }

      return lines.join('<br>');
    },

    buildEdgeTooltip(edge, sourceNode, targetNode, isInternal) {
      const lines = [];

      // Entity-based: show relation type
      if (edge.relation) {
        lines.push(`<b>Relation: ${edge.relation}</b>`);
        if (edge.weight) {
          lines.push(`Confidence: ${edge.weight.toFixed(2)}`);
        }
      } else {
        // Chunk-based: show similarity
        lines.push(`<b>Similarity: ${(edge.weight || 0).toFixed(2)}</b>`);
      }

      // Source/target info with entity names
      const sourceLabel = sourceNode?.entityName || sourceNode?.label || 'Unknown';
      const targetLabel = targetNode?.entityName || targetNode?.label || 'Unknown';
      lines.push(`From: ${sourceLabel} (C${(sourceNode?.group || 0) + 1})`);
      lines.push(`To: ${targetLabel} (C${(targetNode?.group || 0) + 1})`);

      if (!isInternal) {
        lines.push(`<i>Cross-community edge</i>`);
      }

      return lines.join('<br>');
    },

    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    truncateLabel(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength - 3) + '...';
    },

    getGroupColor(groupIndex) {
      return this.groupColors[groupIndex % this.groupColors.length];
    },

    highlightGroup(groupIndex) {
      if (!this.network || !this._sigmaState) return;
      this._sigmaState.setHighlightedGroup(groupIndex);
    },

    resetHighlight() {
      if (!this.network || !this._sigmaState) return;
      this._sigmaState.clearHighlightedGroup();
      this.selectedNode = null;
    },

    fitNetwork() {
      if (!this.network || !this._sigmaState?.fitGraph) return;
      this._sigmaState.fitGraph(true);
    },

    fetchSourceChunks() {
      const self = this;
      if (!self.selectedNode || !self.selectedNode.entityId) {
        return;
      }

      self.loadingChunks = true;
      self.sourceChunks = [];

      self.$call.GetMetaChunksByEntityId(
        self.metaCommunityId,
        self.selectedNode.entityId,
        (response) => {
          self.loadingChunks = false;
          if (response.status === 0 && response.data) {
            self.sourceChunks = response.data;
          }
        },
        (error) => {
          console.error('Error fetching source chunks:', error);
          self.loadingChunks = false;
        }
      );
    },

    truncateContent(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    togglePhysics() {
      this.physicsEnabled = !this.physicsEnabled;
    },

    updateEdgeLabels() {
      if (!this.network) return;
      this.network.setSetting('renderEdgeLabels', this.showEdgeLabels);
      this.network.refresh();
    },

    updateArrows() {
      if (!this.network) return;
      this.network.setSetting('defaultEdgeType', this.showArrows ? 'curvedArrow' : 'curved');
      this.network.refresh();
    },

    updateShadows() {
      if (!this.network) return;
      this.network.refresh();
    },

    // Color utility functions
    darkenColor(hex, percent) {
      const num = parseInt(hex.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = Math.max((num >> 16) - amt, 0);
      const G = Math.max(((num >> 8) & 0x00FF) - amt, 0);
      const B = Math.max((num & 0x0000FF) - amt, 0);
      return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
    },

    lightenColor(hex, percent) {
      const num = parseInt(hex.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = Math.min((num >> 16) + amt, 255);
      const G = Math.min(((num >> 8) & 0x00FF) + amt, 255);
      const B = Math.min((num & 0x0000FF) + amt, 255);
      return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
    },
  },
};
</script>

<style scoped>
.graph-card {
  height: calc(100vh - 150px);
  min-height: 700px;
}

.graph-card :deep(.v-card-text) {
  height: 100%;
  padding: 0 !important;
}

.graph-container {
  position: relative;
  width: 100%;
  height: calc(100vh - 150px);
  min-height: 700px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 150px);
  min-height: 700px;
  background: #f8f9fa;
}

.legend-item {
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.legend-item:hover {
  background-color: rgba(0, 0, 0, 0.08);
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  border: 2px solid rgba(0,0,0,0.1);
}

.summary-text {
  font-style: italic;
  line-height: 1.3;
  margin-top: 2px;
}

.node-content {
  background: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  line-height: 1.4;
}

/* Sigma nav + tooltip */
.graph-container :deep(.sigma-nav) {
  position: absolute;
  right: 16px;
  bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 6;
}

.graph-container :deep(.sigma-nav button) {
  width: 36px;
  height: 36px;
  border: 1px solid #d0d5dd;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
}

.graph-container :deep(.sigma-nav button:hover) {
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.graph-container :deep(.sigma-cross-edges) {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.graph-container :deep(.sigma-tooltip) {
  position: absolute;
  z-index: 7;
  pointer-events: none;
  background-color: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid #ddd !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
  padding: 10px 14px !important;
  font-size: 12px !important;
  max-width: 350px !important;
}
</style>
